(function () {
  const HOST_SELECTOR = '#ShopifyChat';
  const STORAGE_KEY = 'fv_shopify_chat_position_v1';
  const EDGE_GAP = 16;
  const TOP_GAP = 80;
  const BOTTOM_GAP = 20;
  const DRAG_THRESHOLD = 6;

  class DraggableShopifyChat {
    constructor(host) {
      this.host = host;
      this.button = null;
      this.drag = null;
      this.suppressClick = false;
      this.wasExpanded = false;
      this.positionProperties = ['position', 'left', 'top', 'right', 'bottom', 'z-index', 'transition'];
      this.originalInlineStyles = new Map(
        this.positionProperties.map((property) => [
          property,
          {
            value: this.host.style.getPropertyValue(property),
            priority: this.host.style.getPropertyPriority(property)
          }
        ])
      );
      this.boundResize = this.handleViewportResize.bind(this);

      this.host.dataset.fvDraggableChat = 'true';
      this.observeHost();
      this.bindButtonWhenReady();
      window.addEventListener('resize', this.boundResize, { passive: true });
    }

    observeHost() {
      this.resizeObserver = new ResizeObserver(() => this.syncExpandedState());
      this.resizeObserver.observe(this.host);

      if (this.host.shadowRoot) {
        this.shadowObserver = new MutationObserver(() => this.syncExpandedState());
        this.shadowObserver.observe(this.host.shadowRoot, {
          attributes: true,
          attributeFilter: ['aria-expanded', 'class'],
          childList: true,
          subtree: true
        });
      }
    }

    syncExpandedState() {
      const expanded = this.isExpanded();

      if (expanded) {
        this.clearPosition();
      } else if (this.wasExpanded) {
        this.applySavedPosition();
      }

      this.wasExpanded = expanded;
      this.bindButtonWhenReady();
    }

    bindButtonWhenReady() {
      const button = this.host.shadowRoot?.querySelector('.chat-toggle');
      if (!button) {
        window.setTimeout(() => this.bindButtonWhenReady(), 300);
        return;
      }

      if (button.dataset.fvDragBound === 'true') {
        this.button = button;
        return;
      }

      this.button = button;
      button.dataset.fvDragBound = 'true';
      button.style.touchAction = 'none';
      button.style.cursor = 'grab';
      button.title = 'Drag to move chat. Use Alt + arrow keys for keyboard positioning.';

      button.addEventListener('pointerdown', this.handlePointerDown.bind(this));
      button.addEventListener('pointermove', this.handlePointerMove.bind(this));
      button.addEventListener('pointerup', this.handlePointerUp.bind(this));
      button.addEventListener('pointercancel', this.handlePointerCancel.bind(this));
      button.addEventListener('click', this.handleClick.bind(this), true);
      button.addEventListener('keydown', this.handleKeydown.bind(this));

      this.applySavedPosition();
    }

    isExpanded() {
      const root = this.host.shadowRoot;
      const toggle = root?.querySelector('.chat-toggle');
      const rect = this.host.getBoundingClientRect();

      return (
        toggle?.getAttribute('aria-expanded') === 'true' ||
        Boolean(root?.querySelector('[role="dialog"], iframe, .chat-window, .chat-app--open')) ||
        rect.width > 220 ||
        rect.height > 150
      );
    }

    handlePointerDown(event) {
      if (event.button !== 0 || !event.isPrimary || this.isExpanded()) return;

      const rect = this.host.getBoundingClientRect();
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        width: rect.width,
        height: rect.height,
        dragging: false
      };

      this.button.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const dx = event.clientX - this.drag.startX;
      const dy = event.clientY - this.drag.startY;

      if (!this.drag.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

      this.drag.dragging = true;
      this.button.style.cursor = 'grabbing';
      document.documentElement.classList.add('fv-chat-is-dragging');
      event.preventDefault();

      const bounds = this.getBounds(this.drag.width, this.drag.height);
      const x = this.clamp(this.drag.originX + dx, bounds.minX, bounds.maxX);
      const y = this.clamp(this.drag.originY + dy, bounds.minY, bounds.maxY);
      this.setPosition(x, y);
    }

    handlePointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const didDrag = this.drag.dragging;
      const rect = this.host.getBoundingClientRect();
      this.finishPointer(event.pointerId);

      if (!didDrag) return;

      this.suppressClick = true;
      const bounds = this.getBounds(rect.width, rect.height);
      const side = rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right';
      const x = side === 'left' ? bounds.minX : bounds.maxX;
      const y = this.clamp(rect.top, bounds.minY, bounds.maxY);

      this.animateTo(x, y);
      this.savePosition(side, y, bounds);
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }

    handlePointerCancel(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.finishPointer(event.pointerId);
      this.applySavedPosition();
    }

    finishPointer(pointerId) {
      this.button.releasePointerCapture?.(pointerId);
      this.button.style.cursor = 'grab';
      document.documentElement.classList.remove('fv-chat-is-dragging');
      this.drag = null;
    }

    handleClick(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleKeydown(event) {
      if (!event.altKey || this.isExpanded()) return;

      const directions = {
        ArrowLeft: [-24, 0],
        ArrowRight: [24, 0],
        ArrowUp: [0, -24],
        ArrowDown: [0, 24]
      };
      const delta = directions[event.key];
      if (!delta) return;

      event.preventDefault();
      const rect = this.host.getBoundingClientRect();
      const bounds = this.getBounds(rect.width, rect.height);
      const x = this.clamp(rect.left + delta[0], bounds.minX, bounds.maxX);
      const y = this.clamp(rect.top + delta[1], bounds.minY, bounds.maxY);
      this.setPosition(x, y);

      const side = x + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right';
      this.savePosition(side, y, bounds);
    }

    handleViewportResize() {
      if (!this.isExpanded()) this.applySavedPosition();
    }

    getBounds(width, height) {
      const minY = Math.min(TOP_GAP, Math.max(EDGE_GAP, window.innerHeight - height - BOTTOM_GAP));
      return {
        minX: EDGE_GAP,
        maxX: Math.max(EDGE_GAP, window.innerWidth - width - EDGE_GAP),
        minY,
        maxY: Math.max(minY, window.innerHeight - height - BOTTOM_GAP)
      };
    }

    setPosition(x, y) {
      this.host.style.setProperty('position', 'fixed', 'important');
      this.host.style.setProperty('left', `${Math.round(x)}px`, 'important');
      this.host.style.setProperty('top', `${Math.round(y)}px`, 'important');
      this.host.style.setProperty('right', 'auto', 'important');
      this.host.style.setProperty('bottom', 'auto', 'important');
      this.host.style.setProperty('z-index', '2147483647', 'important');
    }

    clearPosition() {
      this.positionProperties.forEach((property) => this.restoreProperty(property));
    }

    restoreProperty(property) {
      const original = this.originalInlineStyles.get(property);
      this.host.style.removeProperty(property);
      if (original?.value) this.host.style.setProperty(property, original.value, original.priority);
    }

    animateTo(x, y) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.host.style.setProperty('transition', 'left 180ms ease-out, top 180ms ease-out', 'important');
      }

      this.setPosition(x, y);
      window.setTimeout(() => this.restoreProperty('transition'), 220);
    }

    savePosition(side, y, bounds) {
      const range = Math.max(bounds.maxY - bounds.minY, 1);
      const yRatio = this.clamp((y - bounds.minY) / range, 0, 1);

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ side, yRatio }));
      } catch (_) {}
    }

    readPosition() {
      try {
        const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        if (!saved || !['left', 'right'].includes(saved.side) || typeof saved.yRatio !== 'number') return null;
        return saved;
      } catch (_) {
        return null;
      }
    }

    applySavedPosition() {
      const saved = this.readPosition();
      if (!saved || this.isExpanded()) return;

      const rect = this.host.getBoundingClientRect();
      const bounds = this.getBounds(rect.width, rect.height);
      const x = saved.side === 'left' ? bounds.minX : bounds.maxX;
      const y = bounds.minY + this.clamp(saved.yRatio, 0, 1) * (bounds.maxY - bounds.minY);
      this.setPosition(x, y);
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  function initializeChat() {
    const host = document.querySelector(HOST_SELECTOR);
    if (!host || host.dataset.fvDraggableChat === 'true') return false;

    new DraggableShopifyChat(host);
    return true;
  }

  if (!initializeChat()) {
    const observer = new MutationObserver(() => {
      if (initializeChat()) observer.disconnect();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();

